import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    createNewLanguageblock,
    editLanguageblockTranslation,
    getAllLanguageBlocks,
    getAllRegionBlocks,
    getDocumentRegionsByLanguageBlockKey,
    getLanguageBlocksByFilter,
    getLanguageBlocksByQuery,
    getLanguages,
    getLocalizedTemplateContent,
    getTranslationsByLanguageBlockKey,
} from 'src/api/templateAdmin';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    CREATE_LANGUAGE_BLOCK,
    EDIT_LANGUAGE_BLOCK_TRANSLATION,
    FETCH_ALL_LANGUAGE_BLOCKS,
    FETCH_ALL_REGION_BLOCKS,
    FETCH_DOCUMENT_REGIONS_BY_TEMPLATE_KEY,
    FETCH_LANGUAGE_BLOCKS_BY_QUERY,
    FETCH_LANGUAGE_BLOCKS_BY_TEMPLATE_KEY,
    FETCH_LANGUAGE_TRANSLATIONS_BY_TEMPLATE_KEY,
    FETCH_TEMPLATE_BY_TEMPLATE_KEY,
    FETCH_TEXT_BLOCK_LANGUAGES,
    requestCreateLanguageBlock,
    requestCreateLanguageBlockFailure,
    requestCreateLanguageBlockSuccess,
    requestEditLanguageBlockTranslation,
    requestEditLanguageBlockTranslationFailure,
    requestEditLanguageBlockTranslationSuccess,
    requestFetchAllLanguageBlocks,
    requestFetchAllLanguageBlocksFailure,
    requestFetchAllLanguageBlocksSuccess,
    requestFetchAllRegionBlocks,
    requestFetchAllRegionBlocksFailure,
    requestFetchAllRegionBlocksSuccess,
    requestFetchDocumentRegionsByTemplateKey,
    requestFetchDocumentRegionsByTemplateKeyFailure,
    requestFetchDocumentRegionsByTemplateKeySuccess,
    requestFetchLanguageBlocksByQuery,
    requestFetchLanguageBlocksByQueryFailure,
    requestFetchLanguageBlocksByQuerySuccess,
    requestFetchLanguageBlocksByTemplateKey,
    requestFetchLanguageBlocksByTemplateKeyFailure,
    requestFetchLanguageBlocksByTemplateKeySuccess,
    requestFetchLanguageTranslationsByTemplateKey,
    requestFetchLanguageTranslationsByTemplateKeyFailure,
    requestFetchLanguageTranslationsByTemplateKeySuccess,
    requestFetchTemplateByTemplateKey,
    requestFetchTemplateByTemplateKeyFailure,
    requestFetchTemplateByTemplateKeySuccess,
    requestFetchTextBlockLanguages,
    requestFetchTextBlockLanguagesFailure,
    requestFetchTextBlockLanguagesSuccess,
} from './actions';

function* fetchCreateLanguageBlock(action: AnyAction): SagaIterator {
    yield put(requestCreateLanguageBlock());

    const { error, response } = yield call(createNewLanguageblock, action.newLanguageBlock);

    if (error) {
        yield put(requestCreateLanguageBlockFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestCreateLanguageBlockSuccess(response));
    }
}

function* fetchEditLanguageBlockTranslation(action: AnyAction): SagaIterator {
    yield put(requestEditLanguageBlockTranslation());

    const { error, response } = yield call(
        editLanguageblockTranslation,
        action.normalizedName,
        action.languageIso2,
        action.text
    );

    if (error) {
        yield put(requestEditLanguageBlockTranslationFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestEditLanguageBlockTranslationSuccess(response));
    }
}

function* fetchGetTemplateByTemplateKey(action: AnyAction): SagaIterator {
    yield put(requestFetchTemplateByTemplateKey());

    const { error, response } = yield call(getLocalizedTemplateContent, action.templateKey);

    if (error) {
        yield put(requestFetchTemplateByTemplateKeyFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestFetchTemplateByTemplateKeySuccess(response));
    }
}

export function* generateCreateLanguageBlock() {
    yield takeLatest(CREATE_LANGUAGE_BLOCK, fetchCreateLanguageBlock);
}

export function* generateEditLanguageBlockTranslation() {
    yield takeLatest(EDIT_LANGUAGE_BLOCK_TRANSLATION, fetchEditLanguageBlockTranslation);
}

export function* generateGetTemplateByTemplateKey() {
    yield takeLatest(FETCH_TEMPLATE_BY_TEMPLATE_KEY, fetchGetTemplateByTemplateKey);
}

/* Marketing Text Blocks */
function* fetchAllLanguageBlocksSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchAllLanguageBlocks());

    const { error, response } = yield call(getAllLanguageBlocks, action.pageable);

    if (error) {
        yield put(requestFetchAllLanguageBlocksFailure(error));
    } else {
        yield put(requestFetchAllLanguageBlocksSuccess(response));
    }
}

export function* generateFetchAllLanguageBlocksWatcher() {
    yield takeLatest(FETCH_ALL_LANGUAGE_BLOCKS, fetchAllLanguageBlocksSaga);
}

function* fetchLanguageBlocksByQuerySaga(action: AnyAction): SagaIterator {
    yield put(requestFetchLanguageBlocksByQuery());

    const { error, response } = yield call(getLanguageBlocksByQuery, action.pageable, action.query);

    if (error) {
        yield put(requestFetchLanguageBlocksByQueryFailure(error));
    } else {
        yield put(requestFetchLanguageBlocksByQuerySuccess(response));
    }
}

export function* generateFetchLanguageBlocksByQueryWatcher() {
    yield takeLatest(FETCH_LANGUAGE_BLOCKS_BY_QUERY, fetchLanguageBlocksByQuerySaga);
}

function* fetchLanguageBlocksByTemplateKeySaga(action: AnyAction): SagaIterator {
    yield put(requestFetchLanguageBlocksByTemplateKey());

    const { error, response } = yield call(getLanguageBlocksByFilter, action.pageable, action.templateKey);

    if (error) {
        yield put(requestFetchLanguageBlocksByTemplateKeyFailure(error));
    } else {
        yield put(requestFetchLanguageBlocksByTemplateKeySuccess(response));
    }
}

export function* generateFetchLanguageBlocksByTemplateKeyWatcher() {
    yield takeLatest(FETCH_LANGUAGE_BLOCKS_BY_TEMPLATE_KEY, fetchLanguageBlocksByTemplateKeySaga);
}

function* fetchLanguageTranslationsByTemplateKeySaga(action: AnyAction): SagaIterator {
    yield put(requestFetchLanguageTranslationsByTemplateKey());

    const { error, response } = yield call(getTranslationsByLanguageBlockKey, action.templateKey);

    if (error) {
        yield put(requestFetchLanguageTranslationsByTemplateKeyFailure(error));
    } else {
        yield put(requestFetchLanguageTranslationsByTemplateKeySuccess(response));
    }
}

export function* generateFetchLanguageTranslationsByTemplateKeyWatcher() {
    yield takeLatest(FETCH_LANGUAGE_TRANSLATIONS_BY_TEMPLATE_KEY, fetchLanguageTranslationsByTemplateKeySaga);
}

function* fetchTextBlockLanguagesSaga(): SagaIterator {
    yield put(requestFetchTextBlockLanguages());

    const { error, response } = yield call(getLanguages);

    if (error) {
        yield put(requestFetchTextBlockLanguagesFailure(error));
    } else {
        yield put(requestFetchTextBlockLanguagesSuccess(response));
    }
}

export function* generateFetchTextBlockLanguagesWatcher() {
    yield takeLatest(FETCH_TEXT_BLOCK_LANGUAGES, fetchTextBlockLanguagesSaga);
}

export function* fetchAllRegionBlocksSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchAllRegionBlocks());

    const { error, response } = yield call(getAllRegionBlocks, action.params);

    if (error) {
        yield put(requestFetchAllRegionBlocksFailure(error));
    } else {
        yield put(requestFetchAllRegionBlocksSuccess(response));
    }
}

export function* generateFetchAllRegionBlocksWatcher() {
    yield takeLatest(FETCH_ALL_REGION_BLOCKS, fetchAllRegionBlocksSaga);
}

function* fetchDocumentRegionsByTemplateKeySaga(action: AnyAction): SagaIterator {
    yield put(requestFetchDocumentRegionsByTemplateKey());

    const { error, response } = yield call(getDocumentRegionsByLanguageBlockKey, action.templateKey);

    if (error) {
        yield put(requestFetchDocumentRegionsByTemplateKeyFailure(error));
    } else {
        yield put(requestFetchDocumentRegionsByTemplateKeySuccess(response));
    }
}

export function* generateFetchDocumentRegionsByTemplateKeyWatcher() {
    yield takeLatest(FETCH_DOCUMENT_REGIONS_BY_TEMPLATE_KEY, fetchDocumentRegionsByTemplateKeySaga);
}
